// src/components/mission-control/agent-terminal/entries/StockDimensionPreviewBlock.tsx
import { STOCK_SHAPES, type ShapeId } from '../../../../lib/stock-shapes'
import StockDimensionInputs from '../../../shared/StockDimensionInputs'
import type { PartPlacement } from '../../../../services/stockAlignment'

interface Props {
  shapeId: ShapeId
  values: Record<string, number>
  units: 'mm' | 'in'
  /** Part geometry needed by the irregular_drawing alignment flow. */
  partGeometry?: Record<string, unknown>
  onChange(values: Record<string, number>): void
  /** Fired when the user confirms part placement inside uploaded stock
   *  (irregular_drawing flow). Forwards both the placement and the captured
   *  stock geometry blob so callers can persist them on the job. */
  onPlacementConfirm?(placement: PartPlacement, stockGeometry: Record<string, unknown>): void
  onSubmit(): void
}

export default function StockDimensionPreviewBlock({
  shapeId, values, units, partGeometry, onChange, onPlacementConfirm, onSubmit,
}: Props) {
  const def = STOCK_SHAPES[shapeId]
  const isIrregularDrawing = shapeId === 'irregular_drawing'

  return (
    <div className="my-2 space-y-3">
      {!isIrregularDrawing && (
        <div className="relative inline-block p-4 rounded-sm border border-slate-800 bg-white/[0.02]">
          <div className="font-mono text-[10px] tracking-widest uppercase text-cyan-400 mb-2">
            {def.label}
          </div>
          <div className="flex items-center justify-center w-[200px] h-[140px]">
            {def.renderSvg('full', values)}
          </div>
          {/* Caret/arrow pointing down to inputs */}
          <div className="absolute left-8 -bottom-2 w-3 h-3 rotate-45 border-r border-b border-slate-800 bg-slate-950" />
        </div>
      )}

      <div className="pl-2">
        <StockDimensionInputs
          shapeId={shapeId}
          values={values}
          onChange={onChange}
          units={units}
          partGeometry={partGeometry}
          onPlacementConfirm={onPlacementConfirm}
        />
        {/* The irregular_drawing flow has its own Confirm button inside the
         *  alignment viewer (it submits via onPlacementConfirm), so the outer
         *  Confirm button would be redundant + confusing. */}
        {!isIrregularDrawing && (
          <button
            type="button"
            onClick={onSubmit}
            className="mt-3 px-3 py-1.5 font-mono text-xs tracking-widest uppercase
                       text-slate-950 bg-cyan-500 hover:bg-cyan-400 rounded-sm transition-colors"
          >
            Confirm
          </button>
        )}
      </div>
    </div>
  )
}
